
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { map } from 'rxjs/operators';
import { DeployedEnv } from "../../env";
import { LogCat, LogManager } from "../../log";
import { CredentialsCache } from "./service.credentials";

export interface IAccessToken {
	accessToken:string;
	expiresIn:number;
	refreshToken?:string;
}
export interface IServerToken {
	access_token:string;
	expires_in:number;
	// the client implementation is ready for supporting refresh_tokens,
	// but they are not used at this point
	refresh_token?:string;
}

@Injectable({ providedIn: 'root' })
  export class AuthProxy {
	constructor(private http:HttpClient, 
				private envService:DeployedEnv,
				private MFATokenProvider: CredentialsCache) {
	}

	refreshToken(refreshToken:string):Observable<IAccessToken>{
		let xmlRequestParam =
			encodeURIComponent("refresh_token") + '=' + encodeURIComponent(refreshToken) + '&' +
			encodeURIComponent("grant_type") + '=' + encodeURIComponent("refresh_token");

		let reqSpec = {
			headers: new HttpHeaders(
				{
					["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
				})
		};

		let urlRequestPromise = this.http.post(this.envService.authUrl() + "/Token", xmlRequestParam, reqSpec);

		let wrappedPromise = urlRequestPromise.pipe(
			map((x:IServerToken) => {
				return {
					accessToken: x.access_token,
					expiresIn: x.expires_in,
					refreshToken:x.refresh_token
				};
			}));

		return wrappedPromise;
	}

	getTokenFromShs(shs:string):Observable<IAccessToken> {
		const xmlRequestParam = encodeURIComponent('userName') + '=' +
			encodeURIComponent(shs) + '&' +
			encodeURIComponent('password') + '=' + encodeURIComponent('') + '&' +
			encodeURIComponent('grant_type') + '=' + encodeURIComponent('password');

		const reqSpec = {
			headers: new HttpHeaders(
				{
					['Content-Type']: 'application/x-www-form-urlencoded;charset=utf-8'
				})
		};

		let urlRequestPromise = this.http.post(this.envService.authUrl() + "/SHSToken", xmlRequestParam, reqSpec);

		let wrappedPromise = urlRequestPromise.pipe(
			map((x:IServerToken) => {
				if (x.expires_in<0){
					LogManager.error(LogCat.AppShell.Login,"shs token as expired");
					throw new Error();
				}
				return {
					accessToken: x.access_token,
					expiresIn: x.expires_in,
					refreshToken:x.refresh_token
				};
			}));

		return wrappedPromise;
	}

	getToken(userName:string, password:string, external_activation:boolean):Observable<IAccessToken> {
		let xmlRequestParam = encodeURIComponent('userName') + '=' +
			encodeURIComponent(userName) + '&' +
			encodeURIComponent("password") + '=' + encodeURIComponent(password) + '&' +
			encodeURIComponent("grant_type") + '=' + encodeURIComponent("password") + '&' +
			encodeURIComponent("external_activation") + '=' + encodeURIComponent(String(external_activation))
		;

		let reqSpec = {
			headers: new HttpHeaders(
				{
					["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
				})
		};

		let urlRequestPromise = this.http.post(this.envService.authUrl() + "/Token", xmlRequestParam, reqSpec);

		let wrappedPromise = urlRequestPromise.pipe(
			map((x:IServerToken) => {
				return {
					accessToken: x.access_token,
					expiresIn: x.expires_in,
					refreshToken:x.refresh_token
				};
			}));

		return wrappedPromise;
	}

	MFAGetToken(code: number, isInitializeTOTP: boolean, resetTOTP: boolean, rememberThisDevice: boolean):Observable<IAccessToken> {
		let xmlRequestParam = encodeURIComponent('userName') + '=' +
			encodeURIComponent(this.MFATokenProvider.MFAToken) + '&' +
			encodeURIComponent("password") + '=' + encodeURIComponent(code) + '&' +
			encodeURIComponent("grant_type") + '=' + encodeURIComponent("password");

			if (isInitializeTOTP) {
				xmlRequestParam +='&' +	encodeURIComponent("initialize_totp") + '=' + encodeURIComponent(true);
			}

			if (resetTOTP) {
				xmlRequestParam +='&' +	encodeURIComponent("reset_totp") + '=' + encodeURIComponent(true);
			}

			if (rememberThisDevice) {
				xmlRequestParam +='&' +	encodeURIComponent("trust_device") + '=' + encodeURIComponent(true);
			}

		let reqSpec = {
			headers: new HttpHeaders(
				{
					["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
				})
		};

		let urlRequestPromise = this.http.post(this.envService.authUrl() + "/MFAToken", xmlRequestParam, reqSpec);

		let wrappedPromise = urlRequestPromise.pipe(
			map((x:IServerToken) => {
				return {
					accessToken: x.access_token,
					expiresIn: x.expires_in,
					refreshToken:x.refresh_token
				};
			}));

		return wrappedPromise;
	}

	changePassword(userName: string, oldPassword: string, newPassword: string): Observable<any> {

		let params = `userName=${encodeURIComponent(userName)}` +
				`&oldPassword=${encodeURIComponent(oldPassword)}&newPassword=${encodeURIComponent(newPassword)}`;
		let reqSpec = {
			headers: new HttpHeaders(
				{
					["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
				})
		};

		return this.http.post(this.envService.authSSOUrl() + "/ChangePassword", params,reqSpec);
	}

	invalidateRefreshToken(refreshToken: string): Observable<any> {
		return this.http.post(this.envService.authUrl() + "/InvalidateRefreshToken", {refreshToken});

	}

	getUserMFAMethods():Observable<any> {
		let headers = new HttpHeaders();
		headers = headers.set("Authorization", "Bearer " + this.MFATokenProvider.MFAToken);

		return this.http.post(this.envService.authSSOUrl() + "/MFA/GetUserMFAMethods", null, {headers: headers});
	}

	getMFAConfigurations():Observable<any> {
		let headers = new HttpHeaders();
		headers = headers.set("Authorization", "Bearer " + this.MFATokenProvider.MFAToken);

		return this.http.post(this.envService.authSSOUrl() + "/MFA/GetConfigurations", null, {headers: headers});
	}

	resendVerificationCode():Observable<any> {
		let headers = new HttpHeaders();
		headers = headers.set("Authorization", "Bearer " + this.MFATokenProvider.MFAToken);

		return this.http.post(this.envService.authSSOUrl() + "/MFA/ResendVerificationCode", null, {headers: headers});
	}

	changeMFAMethod(MFAMethodCode:string, setAsDefaultMFAMethod:boolean):Observable<any> {
		let headers = new HttpHeaders();
		headers = headers.set("Authorization", "Bearer " + this.MFATokenProvider.MFAToken);

		return this.http.post(this.envService.authSSOUrl() + "/MFA/ChangeMFAMethod", 
						{MFAMethodCode:MFAMethodCode, SetAsDefaultMFAMethod:setAsDefaultMFAMethod}, 
						{headers: headers});
	}

	isAllowedSetMFAMethodAsDefault():Observable<any> {
		let headers = new HttpHeaders();
		headers = headers.set("Authorization", "Bearer " + this.MFATokenProvider.MFAToken);

		return this.http.post(this.envService.authSSOUrl() + "/MFA/IsAllowedSetMFAMethodAsDefault", null, {headers: headers});
	}

	initializeTOTP():Observable<any> {
		let headers = new HttpHeaders();
		headers = headers.set("Authorization", "Bearer " + this.MFATokenProvider.MFAToken);

		return this.http.post(this.envService.authSSOUrl() + "/MFA/InitializeTOTP", null, {headers: headers});
	}

	validateUser(userName: string, password:string):Observable<any> {
		let params = `UserName=${encodeURIComponent(userName)}` +
				`&Password=${encodeURIComponent(password)}`;
		let reqSpec = {
			headers: new HttpHeaders(
				{
					["Content-Type"]: 'application/x-www-form-urlencoded;charset=utf-8'
				})
		};
		return this.http.post(this.envService.authSSOUrl() + "/Auth/ValidateUser", params, reqSpec);
	}
}
